import type { FC } from 'react'
import React, { useState, useEffect } from 'react'
import { makeStyles, createStyles } from '@material-ui/core/styles'
import clsx from 'clsx'
import { isInclusivelyBeforeDay } from 'react-dates'
import moment from 'moment'
import 'react-dates/initialize'
import { DateRangePicker } from 'react-dates'
import 'react-dates/lib/css/_datepicker.css'

import Select from '../Select'
import type { ITheme } from '@homeviews/styles'
import { getDateQuickPickOptions } from '@homeviews/utils'

const useStyles = makeStyles((theme: ITheme) =>
  createStyles({
    root: {
      flexGrow: 1,
      display: 'flex',
      alignItems: 'center',
      '& .DateRangePickerInput': {
        display: 'flex',
        alignItems: 'center',
        backgroundColor: 'transparent',
        border: 'none',
      },
      '& .DateInput_input': {
        color: '#4E516B',
        fontSize: '14px',
        letterSpacing: '-0.23px',
        lineHeight: '22px',
        paddingTop: 6,
        paddingBottom: 6,
        fontWeight: 500,
      },
      '& .DateInput': {
        width: 120,
        borderRadius: `${5}px!important`,
        border: '1px solid #4E516B',
        overflow: 'hidden',
        position: 'relative',
        paddingLeft: 25,
        color: '#4E516B',
        '&:before': {
          content: '""',
          backgroundImage: 'url("assets/icons/calendar.svg")',
          backgroundRepeat: 'no-repeat',
          backgroundSize: 20,
          position: 'absolute',
          top: 7,
          left: 8,
          width: 20,
          height: 20,
        },
      },
      '& .DateRangePickerInput_arrow': {
        width: 10,
        overflow: 'hidden',
        opacity: 0,
      },
      '& .DateInput_input__focused': {
        borderBottom: 'initial',
      },
      '& .CalendarDay__selected:active': {
        color: `${theme.colors.primary}!important`,
      },
      '& .CalendarDay__hovered': {
        color: `#D0E2E0!important`,
      },
      '& .CalendarDay__selected': {
        color: `#D0E2E0!important`,
      },
    },
    select: {
      marginLeft: 5,
      width: 165,
    },
    quickPickSeparator: {
      color: theme.colors.grey600,
      fontSize: 14,
      fontWeight: 500,
      letterSpacing: '-0.2px',
      lineHeight: '22px',
      marginLeft: 8,
      marginRight: 8,
    },
  }),
)

interface IProps {
  initialStartDate?: string
  initialEndDate?: string
  className?: string
  onChange?: any
  withQuickPick?: boolean
  onQuickPickOptionsChange?: any
  onStartDateChange?: (value?: string) => void
  onEndDateChange?: (value?: string) => void
}

const CustomDatePicker: FC<IProps> = ({
  className,
  initialStartDate,
  initialEndDate,
  onChange,
  withQuickPick,
  onQuickPickOptionsChange,
  onStartDateChange,
  onEndDateChange,
  ...rest
}) => {
  const classes = useStyles()
  const [focusedInput, setFocusedInput] = useState<boolean>(null)
  const [selectedDate, setSelectedDate] = useState(null)

  useEffect(() => {
    if (!selectedDate?.startDate || !selectedDate?.endDate) {
      return
    }
    const payload = {
      date_from: selectedDate?.startDate
        ? selectedDate.startDate.format('YYYY-MM-DD')
        : null,
      date_to: selectedDate?.endDate
        ? selectedDate.endDate.format('YYYY-MM-DD')
        : null,
    }
    // trigger on change only if both inputs set
    if (
      (payload.date_from && payload.date_to) ||
      (!payload.date_from && !payload.date_to)
    ) {
      onChange(payload)
    }
  }, [selectedDate?.startDate, selectedDate?.endDate])

  useEffect(() => {
    if (!initialStartDate && !initialEndDate) {
      setSelectedDate(null)
      return
    }

    if (initialStartDate && initialEndDate) {
      const newStartDate = moment(initialStartDate)
      const newEndDate = moment(initialEndDate)

      setSelectedDate({
        startDate: newStartDate,
        endDate: newEndDate,
      })
    }
  }, [initialStartDate, initialEndDate])

  const handleDateChange = (newDate) => {
    setSelectedDate(newDate)

    if (newDate?.startDate && onStartDateChange) {
      onStartDateChange(newDate?.startDate?.format('DD/MM/YYYY'))
    }
    if (newDate?.sendDate && onEndDateChange) {
      onEndDateChange(newDate?.endDate?.format('DD/MM/YYYY'))
    }
  }

  const dateQuickPickOptions = getDateQuickPickOptions()

  return (
    <div className={clsx(classes.root, className)}>
      <DateRangePicker
        startDate={selectedDate?.startDate}
        startDateId="hv-start-date"
        endDate={selectedDate?.endDate}
        endDateId="hv-end-date"
        onDatesChange={handleDateChange}
        focusedInput={focusedInput}
        onFocusChange={(focusedInput) => setFocusedInput(focusedInput)}
        isOutsideRange={(day) => !isInclusivelyBeforeDay(day, moment())}
        displayFormat={() => 'DD/MM/YYYY'}
        {...rest}
      />
      {withQuickPick && (
        <>
          <p className={classes.quickPickSeparator}>Or</p>
          <div className={classes.select}>
            <Select
              onChange={onQuickPickOptionsChange && onQuickPickOptionsChange}
              value={initialStartDate}
              options={dateQuickPickOptions}
              placeholder={'Date range presets'}
            />
          </div>
        </>
      )}
    </div>
  )
}

export default CustomDatePicker
